import {
    demoUrl,
    docsUrl,
    feedbackUrl,
} from '@/config';
import i18n from '@/i18n';
import { Plugins } from '@/plugins';
import { store } from '@/store';
import AgencyApi from '@api/AgencyApi';
import { pages } from '@content/prerender/pages';
import App from '@js/App';
import FontUtil from '@utils/FontUtil';
import PosthogUtil from '@utils/PosthogUtil';
import Vue from 'vue';
import VueRouter from 'vue-router';

import AccountRoutes from '@routes/AccountRoutes';
import AgencyRoutes from '@routes/AgencyRoutes';
import AgencySettingsRoutes from '@routes/AgencySettingsRoutes';
import AppRoutes from '@routes/AppRoutes';
import AuthRoutes from '@routes/AuthRoutes';
import CustomRoutes from '@routes/CustomRoutes';
import DocumentRoutes from '@routes/DocumentRoutes';
import ErrorRoutes from '@routes/ErrorRoutes';
import IndexRoutes from '@routes/IndexRoutes';
import LegalRoutes from '@routes/LegalRoutes';
import OpenRoutes from '@routes/OpenRoutes';
import SettingsRoutes from '@routes/SettingsRoutes';

import '@/filters';
import '@/mixins';
import '@sass/app.scss';

async function main() {
    const domain = window.location.hostname.toLowerCase().replace('www.', '');
    const port = window.location.port;

    FontUtil.applyBrandFont();

    /**
     * Plugins
     */

    Plugins.initPlugins();
    Plugins.initConsent(store);

    /**
     * Agency
     */

    if (domain !== process.env.VUE_APP_DOMAIN) {
        await AgencyApi.getAgencyByDomain({ domain: domain + (port ? `:${port}` : '') })
            .then((response) => {
                store.commit('setAgency', response.data.data);
            }, () => {
                store.commit('setAgency', null);
            });
    } else {
        store.commit('setAgency', null);
    }

    /**
     * Config
     */

    Vue.config.devtools = process.env.NODE_ENV !== 'production';
    Vue.config.performance = false;
    Vue.config.productionTip = false;
    Vue.config.silent = process.env.NODE_ENV === 'production';
    Vue.prototype.$brand = process.env.VUE_APP_BRAND;
    Vue.prototype.$domain = process.env.VUE_APP_DOMAIN;
    Vue.prototype.$url = process.env.VUE_APP_URL;
    Vue.prototype.$apiUrl = process.env.VUE_APP_API_URL;
    Vue.prototype.$demoUrl = demoUrl;
    Vue.prototype.$docsUrl = docsUrl;
    Vue.prototype.$feedbackUrl = feedbackUrl;
    Vue.use(VueRouter);

    /**
     * Components
     */

    // Global components
    Vue.component('v-alert', require('@cmp/Alert').default);
    Vue.component('v-alert-upgrade', require('@cmp/AlertUpgrade').default);
    Vue.component('v-browser-bar', require('@cmp/BrowserBar').default);
    Vue.component('v-dropdown', require('@cmp/Dropdown').default);
    Vue.component('v-icon', require('@cmp/Icon').default);
    Vue.component('v-loader', require('@cmp/Loader').default);
    Vue.component('v-notice', require('@cmp/Notice').default);
    Vue.component('v-review', require('@cmp/Review').default);
    Vue.component('v-table', require('@cmp/Table').default);
    Vue.component('v-table-th', require('@cmp/TableTh').default);
    // Forms
    Vue.component('v-button', require('@forms/Button').default);
    Vue.component('v-button-icon', require('@forms/ButtonIcon').default);
    Vue.component('v-button-toolbar', require('@forms/ButtonToolbar').default);
    Vue.component('v-checkbox', require('@forms/Checkbox').default);
    Vue.component('v-checkbox-group', require('@forms/CheckboxGroup').default);
    Vue.component('v-form-group', require('@forms/FormGroup').default);
    Vue.component('v-input', require('@forms/Input').default);
    Vue.component('v-input-group', require('@forms/InputGroup').default);
    Vue.component('v-multiselect', require('@forms/Multiselect').default);
    Vue.component('v-segmented', require('@forms/Segmented').default);
    Vue.component('v-select', require('@forms/Select').default);
    Vue.component('v-switch', require('@forms/Switch').default);
    Vue.component('v-textarea', require('@forms/Textarea').default);
    /**
     * Router
     */

    // Define routes.
    let routes = [];
    if (domain === process.env.VUE_APP_DOMAIN) {
        routes = [
            IndexRoutes,
            ...LegalRoutes,
            ...DocumentRoutes,
            AuthRoutes,
            AppRoutes,
            AccountRoutes,
            SettingsRoutes,
            AgencySettingsRoutes,
            OpenRoutes,
            ErrorRoutes,
        ];
    } else if (store.getters.agency) {
        routes = [
            AgencyRoutes,
            AuthRoutes,
            AppRoutes,
            AccountRoutes,
            SettingsRoutes,
            AgencySettingsRoutes,
            OpenRoutes,
            ErrorRoutes,
        ];
    } else {
        routes = [
            CustomRoutes,
            ErrorRoutes,
        ];
    }

    // Router.
    const router = new VueRouter({
        mode: 'history',
        scrollBehavior() { return { x: 0, y: 0 }; },
        routes: routes,
    });
    window.router = router;

    // Before hook.
    router.beforeEach(async(to, from, next) => {
        if (!to.matched.length) next();
        for (let route of to.matched) {
            if (route.meta?.middleware) {
                let middlewares = Array.isArray(route.meta.middleware) ? route.meta.middleware : [route.meta.middleware];
                for (let middleware of middlewares) {
                    let params = await middleware({ to, from, store });
                    if (params) {
                        router.push(params).then(() => next()).catch(error => {
                            console.log(error);
                            next();
                        });
                        return;
                    }
                }
            }
        }
        next();
    });

    // After hook.
    router.afterEach((to, from, next) => {
        // Meta.
        if (domain === process.env.VUE_APP_DOMAIN && !['review', 'kiosk', 'showcase'].includes(to.name)) {
            if (to.meta?.document) {
                const page = pages.find((page) => page.name === to.meta.document);
                document.title = page ? page.title : process.env.VUE_APP_TITLE;
            } else if (to.meta?.title) {
                if (Array.isArray(to.meta.title)) {
                    document.title = to.meta.title.map(part => i18n.t(part)).join(' | ') + ' | ' + process.env.VUE_APP_BRAND;
                } else {
                    document.title = i18n.t(to.meta.title) + ' | ' + process.env.VUE_APP_BRAND;
                }
            } else {
                document.title = process.env.VUE_APP_TITLE;
            }
        }
        // Hide modal on real navigation. Query-only updates (e.g. setup steps)
        // must not dismiss layout modals like logout confirm.
        if (store.getters.modal && to.name !== from.name) {
            store.commit('setModal', null);
        }
        // Track pageview.
        PosthogUtil.pageview();
        Plugins.syncMarketingContext(store.state.user);
        if (Plugins.shouldLoadMgrMarketing(to.path) && window.cp) {
            window.cp('page');
        }
    });

    // Error hook — recover from stale/missing async chunks after deploys.
    router.onError(error => {
        const message = error && error.message ? error.message : '';
        if (/loading chunk [\w-]+ failed/i.test(message) || /Failed to fetch dynamically imported module/i.test(message)) {
            window.location.reload();
        }
    });

    /**
     * App
     */

    window.i18n = i18n;
    new Vue({ i18n, router, store, render: h => h(App) }).$mount('#app');
}

main();